import React, { useState, useRef, useEffect } from "react";

interface PositionItem {
  id: number;
  name: string;
  [key: string]: any;
}

interface PositionAutocompleteProps {
  positions: PositionItem[];
  value: number;
  onChange: (positionId: number) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
  /** Si true, no envuelve en row/col con label (para usar dentro de dropdown) */
  inline?: boolean;
}

const PositionAutocomplete: React.FC<PositionAutocompleteProps> = ({
  positions,
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar puesto...",
  label = "Puesto",
  className = "",
  inline = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredPositions, setFilteredPositions] = useState<PositionItem[]>(positions);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredPositions(positions);
    } else {
      const filtered = positions.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPositions(filtered);
    }
  }, [searchTerm, positions]);

  const selectedPosition = positions.find((p) => p.id === value);
  const displayValue = selectedPosition ? selectedPosition.name : "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (position: PositionItem) => {
    onChange(position.id);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(-1);
    setSearchTerm("");
  };

  const inputContent = (
    <div className={inline ? "" : "position-relative"} ref={dropdownRef}>
      {label && (
        <label className="form-label mb-0" style={inline ? { fontSize: "0.9rem" } : undefined}>
          {label}:
        </label>
      )}
      <div className={inline ? "position-relative mt-1" : "mt-1"}>
        <div className="input-group">
          <input
            type="text"
            className="form-control form-control-sm px-3"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              fontSize: "13px",
            }}
            placeholder={displayValue || placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
          />
          {value !== -1 && value != null && !disabled && (
            <div
              className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 end-0"
              style={{ zIndex: 10, cursor: "pointer" }}
              onClick={handleClear}
              title="Limpiar selección"
            >
              <i className="fa fa-times text-muted" style={{ fontSize: "12px" }} />
            </div>
          )}
        </div>

        {isOpen && !disabled && (
          <div
            className="position-absolute w-100 bg-white border rounded shadow-lg"
            style={{
              zIndex: 1000,
              maxHeight: "220px",
              overflowY: "auto",
              marginTop: "4px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}
          >
            {filteredPositions.length > 0 ? (
              filteredPositions.map((position) => (
                <div
                  key={position.id}
                  className="px-3 py-2"
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    borderBottom: "1px solid #f0f0f0",
                    fontSize: "13px",
                  }}
                  onClick={() => handleSelect(position)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)";
                    e.currentTarget.style.transform = "translateX(4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.transform = "translateX(0)";
                  }}
                >
                  <span className="fw-500">{position.name}</span>
                </div>
              ))
            ) : (
              <div
                className="px-3 py-3 text-center text-muted"
                style={{ fontSize: "13px" }}
              >
                <i className="fa fa-search me-2" />
                No se encontraron puestos
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (inline) {
    return <div className={className}>{inputContent}</div>;
  }

  return (
    <div className={className}>
      {inputContent}
    </div>
  );
};

export default PositionAutocomplete;
